import 'yet-another-react-lightbox/styles.css';
import Image from 'next/image';
import {
  disclaimerTextNoSubsIncluded,
  isLoadedVsEnclosure
} from '@helpers/products/vehicle-specific.mjs';
import Lightbox, {
  isImageFitCover,
  isImageSlide,
  useLightboxProps,
  useLightboxState
} from 'yet-another-react-lightbox';

export const ModalGalleryV3_YARLB = ({
  sku,
  images,
  viewerIsOpen,
  closeLightbox,
  photoIndex,
  setPhotoIndex
}) => {
  if (!images?.length) return null;

  const vsDisclaimerText = disclaimerTextNoSubsIncluded;

  const sanitizedImages = images.map((image, index) => {
    // console.log('[ModalGalleryV3_YARLB] image:', image);
    const fileNameContainsInVehicle = image?.url?.toLowerCase().includes('vehicle');
    const isEmptyVsEnclosure = !isLoadedVsEnclosure(sku);
    const shouldDisplayDisclaimer = fileNameContainsInVehicle && isEmptyVsEnclosure;
    // console.log("[ModalGalleryV3_YARLB] fileNameContainsInVehicle:", fileNameContainsInVehicle)
    // console.log("[ModalGalleryV3_YARLB] isEmptyVsEnclosure:", isEmptyVsEnclosure)
    let caption = image?.caption ?? null;
    if (shouldDisplayDisclaimer) {
      caption = vsDisclaimerText;
    }
    // console.log('[ModalGalleryV3_YARLB] shouldDisplayDisclaimer:', shouldDisplayDisclaimer);
    let altText;
    if (sku && !altText) {
      altText = `Product Image of ${sku} ${index + 1}`;
    } else {
      altText = `Image ${index + 1}`;
    }

    const sanitizedImage = {
      index: index,
      url: image?.url ?? image?.image_url ?? image?.src,
      width: image?.width ?? 1600,
      height: image?.height ?? 900,
      alt_text: altText,
      caption: caption
    };
    // console.log('[ModalGalleryV3_YARLB] sanitizedImage:', sanitizedImage);
    return sanitizedImage;
  });

  return (
    <>
      {viewerIsOpen && (
        <Lightbox
          open={viewerIsOpen}
          close={closeLightbox}
          slides={sanitizedImages}
          index={photoIndex}
          on={{
            view: ({index}) => setPhotoIndex(index)
          }}
          carousel={{
            preload: 3,
            padding: 0,
            finite: true
          }}
          render={{
            slide: ({slide, rect, offset}) => (
              <NextJsImageLoaderForReactLightBox slide={slide} rect={rect} offset={offset} />
            ),
            iconPrev: () => <LightboxPrevIcon />,
            iconNext: () => <LightboxNextIcon />,
            iconClose: () => <LightboxCloseIcon />
          }}
          className={'lightbox'}
        />
      )}
    </>
  );
};

const NextJsImageLoaderForReactLightBox = ({slide, offset, rect}) => {
  const {
    on: {click},
    carousel: {imageFit}
  } = useLightboxProps();

  const {currentIndex} = useLightboxState();

  const cover = isImageSlide(slide) && isImageFitCover(slide, imageFit);

  if (!isNextJsImage(slide)) return undefined;

  const width = !cover
    ? Math.round(Math.min(rect?.width, (rect?.height / slide?.height) * slide?.width))
    : rect?.width;

  const height = !cover
    ? Math.round(Math.min(rect?.height, (rect?.width / slide?.width) * slide?.height))
    : rect?.height;

  const imgUrl = slide?.url;

  return (
    <div
      className="relative h-full w-full" /*style={{position: 'relative', width: '100%', height: '100%'}}*/
    >
      <Image
        alt={slide?.alt_text || 'Image'}
        src={imgUrl}
        loading="lazy"
        draggable={false}
        placeholder={slide?.blurDataURL ? 'blur' : undefined}
        style={{
          objectFit: 'contain',
          cursor: click ? 'pointer' : undefined,
          width: '100%',
          height: '100%'
        }}
        width={1200}
        height={1200}
        sizes={`${Math.ceil((width / window.innerWidth) * 100)}vw`}
        onClick={offset === 0 ? () => click?.({index: currentIndex}) : undefined}
      />
      {slide?.caption && (
        <div
          style={{
            position: 'absolute',
            bottom: '0',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            textAlign: 'center',
            fontSize: '22px',
            bottom: 0,
            padding: '10px',
            borderRadius: '8px',
            width: '100%'
          }}>
          {slide?.caption}
        </div>
      )}
    </div>
  );
};

const LightboxPrevIcon = () => {
  return (
    <div className="rounded-full bg-neutral-500 bg-opacity-80 md:border border-white p-2 drop-shadow">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
      </svg>
    </div>
  );
};

const LightboxNextIcon = () => {
  return (
    <div className="rounded-full bg-neutral-500 bg-opacity-80 md:border border-white p-2 drop-shadow">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
};

const LightboxCloseIcon = () => {
  return (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        className="w-8 h-8">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={3}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </div>
  );
};

const isNextJsImage = (slide) => {
  return (
    isImageSlide(slide) && typeof slide?.width === 'number' && typeof slide?.height === 'number'
  );
};
